import { useCallback } from "react";
import { useExportGate } from "@/hooks/useExportGate";
import { useUsageGate } from "@/hooks/useUsageGate";
import { runGatedShare } from "@/lib/shareGate";

/**
 * For a Share / Copy-result / Send button of a tool. Wrap the work:
 *   const { gateShare } = useShareGate();
 *   const copy = () => gateShare(async () => { await navigator.clipboard.writeText(text); toast.success("Copied"); return true; });
 * checkExport() first (paywall and nothing shared at 0 remaining), then the visit's one use is taken only once
 * the share really happened. Same for Premium (2/day) and Professional (10/day) tools. Return false (or throw)
 * from the callback when nothing was shared. Never wrap a share of the site itself or a copy of non-result UI.
 */
export function useShareGate() {
  const { checkExport } = useExportGate();
  const { known, paid, toolId, tryUse, tryUseExport } = useUsageGate();
  // `key` (lib/exportCharge exportKey of what is being delivered): the same unchanged output delivered again in a
  // visit is free; a changed output costs another use, taken only once it was delivered. Also used to wrap a file
  // download that must be charged at delivery (return true from the callback after downloadBlob).
  const gateShare = useCallback(
    (doShare: () => Promise<boolean | void>, key?: string): Promise<boolean> =>
      runGatedShare(
        { toolId, counted: known && !paid, checkExport: () => checkExport(key), charge: () => (key ? tryUseExport(key) : tryUse()) },
        doShare,
      ),
    [toolId, known, paid, checkExport, tryUse, tryUseExport],
  );
  return { gateShare };
}
